import React, { useState } from 'react';
import { Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, UserPlus, MailCheck } from 'lucide-react';
import SEO from '../components/SEO';
import { useAuth } from '../context/AuthContext';

const Signup = () => {
  const { signUp, resendConfirmation, isAuthenticated, authMode } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || '/saved';

  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError]       = useState('');
  const [needsConfirmation, setNeedsConfirmation] = useState(false);
  const [resending, setResending] = useState(false);
  const [resentAt, setResentAt]   = useState(null);

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (!email || !password) { setError('Enter your email and password.'); return; }
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }

    setSubmitting(true);
    try {
      const { needsConfirmation: needs } = await signUp(email, password);
      if (needs) {
        setNeedsConfirmation(true);
        toast.success('Account created — check your inbox to confirm.');
      } else {
        toast.success('Welcome to GIWAHS.');
        navigate(from, { replace: true });
      }
    } catch (err) {
      setError(err?.message || 'Sign up failed.');
    } finally {
      setSubmitting(false);
    }
  };

  const ic = 'giwahs-auth-input mt-2';

  return (
    <div data-testid="signup-page" className="surface-obsidian">
      <SEO
        title="Create your GIWAHS buyer account"
        description="Sign up for a free GIWAHS buyer account — save factories, compare suppliers, and track your RFQs."
        noindex
      />
      <section className="border-b border-hairline">
        <div className="mx-auto grid max-w-[1440px] gap-12 px-5 py-16 md:grid-cols-12 md:px-8 md:py-24">
          <div className="md:col-span-5">
            <span className="label-overline text-gold">/ Buyer Account</span>
            <h1 className="mt-4 font-display tracking-tighter text-platinum" style={{ fontWeight: 700, fontSize: 'clamp(2.25rem, 5vw, 4.5rem)', lineHeight: 0.95 }}>
              Create your account.
            </h1>
            <p className="mt-5 max-w-md text-platinum-2">
              Free for buyers. No card required. Save factories, compare suppliers side-by-side, track your RFQs.
            </p>
            <div className="mt-8 space-y-3 border-t border-hairline pt-5 text-sm text-platinum-2">
              <p>• Persist your saved & compare lists across devices.</p>
              <p>• Faster RFQs — your details pre-fill.</p>
              <p>• Get matched with newly-verified factories in your sourcing categories.</p>
            </div>
            <div className="mt-8 border-t border-hairline pt-5 text-sm text-muted-warm">
              Already a buyer? <Link to="/login" data-testid="signup-to-login-link" className="underline underline-offset-4 text-platinum-2 hover:text-gold transition-colors">Sign in</Link>.
            </div>
            {authMode === 'mock' && (
              <div data-testid="signup-mock-mode-notice" className="giwahs-auth-notice mt-6 p-3 text-xs">
                <span className="label-overline text-gold">Dev mode</span>
                <p className="mt-1 text-platinum-2">Auth is running in local mock mode — accounts persist to your browser only.</p>
              </div>
            )}
          </div>

          <div className="md:col-span-7">
            {needsConfirmation ? (
              <div data-testid="signup-needs-confirmation" className="giwahs-auth-panel p-6 md:p-10">
                <span className="label-overline text-gold">Check your inbox</span>
                <h2 className="mt-3 font-display text-2xl tracking-tight text-platinum">We sent you a confirmation link.</h2>
                <p className="mt-3 text-sm text-platinum-2">Click the link in the email we just sent to <span className="font-mono-data text-gold">{email}</span> to activate your account, then come back and sign in.</p>
                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <Link to="/login" className="btn-giwahs btn-giwahs-secondary">Go to sign in</Link>
                  <button
                    data-testid="signup-resend-confirmation-btn"
                    type="button"
                    onClick={async () => {
                      if (resending) return;
                      setResending(true);
                      try {
                        const r = await resendConfirmation(email);
                        setResentAt(Date.now());
                        toast.success(r?.skipped
                          ? 'Mock mode — no email actually sent.'
                          : 'Confirmation email resent — check your inbox.');
                      } catch (err) {
                        toast.error(err?.message || 'Could not resend the confirmation email.');
                      } finally {
                        setResending(false);
                      }
                    }}
                    className="btn-giwahs btn-giwahs-secondary"
                    disabled={resending}
                  >
                    {resending
                      ? <><Loader2 size={14} className="animate-spin" /> Resending…</>
                      : <><MailCheck size={14} strokeWidth={1.5} /> Resend confirmation</>}
                  </button>
                </div>
                {resentAt && (
                  <p data-testid="signup-resend-status" className="mt-3 text-xs text-muted-warm">
                    Last resent {new Date(resentAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}.
                  </p>
                )}
              </div>
            ) : (
              <form data-testid="signup-form" onSubmit={submit} className="giwahs-auth-panel space-y-5 p-6 md:p-10">
                <div>
                  <label className="label-overline text-gold">Email</label>
                  <input
                    data-testid="signup-email-input"
                    type="email"
                    autoComplete="email"
                    className={ic}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label className="label-overline text-gold">Password <span className="text-muted-warm">(min 6)</span></label>
                  <input
                    data-testid="signup-password-input"
                    type="password"
                    autoComplete="new-password"
                    className={ic}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={submitting}
                  />
                </div>
                <div>
                  <label className="label-overline text-gold">Confirm password</label>
                  <input
                    data-testid="signup-confirm-input"
                    type="password"
                    autoComplete="new-password"
                    className={ic}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    disabled={submitting}
                  />
                </div>

                {error && (
                  <div data-testid="signup-error" role="alert" aria-live="polite" className="giwahs-auth-error px-3 py-2 text-sm">
                    {error}
                  </div>
                )}

                <button
                  data-testid="signup-submit-btn"
                  type="submit"
                  disabled={submitting}
                  className="btn-giwahs btn-giwahs-primary w-full justify-center"
                >
                  {submitting ? <><Loader2 size={14} className="animate-spin" /> Creating account…</> : <><UserPlus size={14} strokeWidth={1.5} /> Create account</>}
                </button>

                <p className="text-xs text-muted-warm">
                  By signing up you agree to our <Link to="/terms" className="underline hover:text-gold transition-colors">Terms</Link> and <Link to="/privacy" className="underline hover:text-gold transition-colors">Privacy Policy</Link>.
                </p>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Signup;
